import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, FileSpreadsheet, KeyRound, RefreshCw, Upload, Zap } from "lucide-react";
import { apiKeysApi, mt5Api, type ImportPlatform, type Mt5ImportRecord } from "../lib/api";
import { formatDate } from "../lib/format";
import { useI18n } from "../lib/i18n";

export default function ImportPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mt5-imports"], queryFn: () => mt5Api.list() });
  const [tab, setTab] = useState<ImportPlatform | "auto">("mt5");
  // The upload form only ever deals with a real platform.
  const platform: ImportPlatform = tab === "auto" ? "mt5" : tab;
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  // MT5 exports a workbook; MT4 only offers HTML (File -> Save as Report).
  const accept = platform === "mt4" ? ".htm,.html" : ".xlsx,.xls";

  const uploadM = useMutation({
    mutationFn: (f: File) => mt5Api.upload(f, platform),
    onSuccess: () => {
      setFile(null);
      qc.invalidateQueries({ queryKey: ["mt5-imports"] });
      qc.invalidateQueries({ queryKey: ["trades"] });
      qc.invalidateQueries({ queryKey: ["wallet"] });
      qc.invalidateQueries({ queryKey: ["journey"] });
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <>
      <header>
        <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("import.eyebrow")}</p>
        <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-accent" />
          {t("import.title")}
        </h1>
      </header>

      <section className="glass p-6">
        <div role="tablist" aria-label={t("import.title")} className="flex gap-1 mb-5 p-1 rounded-lg border border-glass w-fit">
          {(["mt5", "mt4", "auto"] as const).map((p) => (
            <button
              key={p}
              role="tab"
              type="button"
              aria-selected={tab === p}
              onClick={() => {
                if (p === tab) return;
                // Switching tabs drops a staged file: the accepted extensions
                // differ, so keeping it would guarantee a server rejection.
                setTab(p);
                setFile(null);
                setError(null);
                uploadM.reset();
              }}
              className={
                "px-4 py-1.5 rounded-md text-sm font-semibold transition-colors flex items-center gap-1.5 " +
                (tab === p
                  ? "bg-gold-500/15 text-accent"
                  : "text-parchment-muted hover:text-parchment")
              }
            >
              {p === "auto" && <Zap className="w-3.5 h-3.5" />}
              {t(p === "auto" ? "import.tabAuto" : p === "mt4" ? "import.tabMt4" : "import.tabMt5")}
            </button>
          ))}
        </div>

        {tab === "auto" ? (
          <AutoSyncPanel />
        ) : (
        <>
        <p className="text-parchment-muted text-sm mb-4">
          {t(platform === "mt4" ? "import.helpMt4" : "import.help")}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <label className="btn-ghost cursor-pointer">
            <input
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => {
                setError(null);
                setFile(e.target.files?.[0] ?? null);
              }}
            />
            <Upload className="w-4 h-4" />
            {file ? file.name : t(platform === "mt4" ? "import.chooseFileMt4" : "import.chooseFile")}
          </label>
          <button
            type="button"
            onClick={() => {
              setError(null);
              if (file) uploadM.mutate(file);
            }}
            disabled={!file || uploadM.isPending}
            className="btn-gold"
          >
            {uploadM.isPending ? t("import.uploading") : t("import.importBtn")}
          </button>
        </div>

        {error && (
          <p className="mt-3 rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
            {error}
          </p>
        )}
        {uploadM.isSuccess && uploadM.data && (
          <p className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-sm text-pnl-pos">
            {t("import.importSuccess", {
              inserted: uploadM.data.import.trades_inserted,
              skipped: uploadM.data.import.trades_skipped,
              wallet: uploadM.data.import.wallet_entries_inserted,
            })}
          </p>
        )}
        </>
        )}
      </section>

      <section className="glass p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-parchment mb-3">{t("import.recentImports")}</h2>
        {q.isLoading && <p className="text-parchment-muted text-sm">{t("common.loading")}</p>}
        {(q.data?.imports.length ?? 0) === 0 && !q.isLoading && (
          <p className="text-parchment-muted text-sm">{t("import.noImports")}</p>
        )}
        <ul className="divide-y divide-glass">
          {(q.data?.imports ?? []).map((i) => (
            <ImportRow key={i.id} row={i} />
          ))}
        </ul>
      </section>
    </>
  );
}

/**
 * Auto-sync setup: issue the API key the MetaTrader EA authenticates with,
 * and hand over the EA source plus install steps.
 *
 * The plaintext key is returned by the server exactly once, at creation, so it
 * is held in local state and shown until the user navigates away. There is no
 * "show key again" -- only regenerate, which invalidates the old one.
 */
function AutoSyncPanel() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [plain, setPlain] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const keysQ = useQuery({ queryKey: ["api-keys"], queryFn: () => apiKeysApi.list() });
  const existing = keysQ.data?.keys?.[0] ?? null;

  const createM = useMutation({
    mutationFn: () => apiKeysApi.create(),
    onSuccess: (d) => {
      setPlain(d.plain);
      setCopied(false);
      qc.invalidateQueries({ queryKey: ["api-keys"] });
    },
  });

  async function copyKey() {
    if (!plain) return;
    try {
      await navigator.clipboard.writeText(plain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked (insecure context, permissions). The key is
      // on screen and selectable, so this is not worth surfacing as an error.
    }
  }

  return (
    <div className="grid gap-5">
      <p className="text-parchment-muted text-sm">{t("import.autoHelp")}</p>

      {/* Step 1 — key */}
      <div className="rounded-xl border border-glass p-4">
        <h3 className="font-semibold text-parchment flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-accent" />
          {t("import.autoStep1")}
        </h3>
        <p className="text-xs text-parchment-muted mb-3">{t("import.autoStep1Sub")}</p>

        {plain ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <code className="flex-1 min-w-0 truncate rounded-lg border border-glass-mid px-3 py-2 font-mono text-xs text-parchment">
                {plain}
              </code>
              <button type="button" onClick={copyKey} className="btn-ghost">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? t("import.copied") : t("import.copy")}
              </button>
            </div>
            <p className="mt-2 text-xs text-amber-300">{t("import.autoKeyOnce")}</p>
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => createM.mutate()}
              disabled={createM.isPending}
              className="btn-gold"
            >
              {existing ? <RefreshCw className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
              {createM.isPending
                ? t("common.saving")
                : existing
                  ? t("import.autoRegenerate")
                  : t("import.autoGenerate")}
            </button>
            {existing && (
              <span className="text-xs text-parchment-muted">
                {t("import.autoExisting", { last4: existing.last_four })}
                {existing.last_used_at
                  ? " · " + t("import.autoLastSeen", { when: formatDate(existing.last_used_at) })
                  : " · " + t("import.autoNeverUsed")}
              </span>
            )}
          </div>
        )}
        {existing && !plain && (
          <p className="mt-2 text-xs text-parchment-muted">{t("import.autoRegenWarn")}</p>
        )}
        {createM.isError && (
          <p className="mt-2 text-sm text-pnl-neg">{(createM.error as Error).message}</p>
        )}
      </div>

      {/* Step 2 — download */}
      <div className="rounded-xl border border-glass p-4">
        <h3 className="font-semibold text-parchment flex items-center gap-2 mb-1">
          <Download className="w-4 h-4 text-accent" />
          {t("import.autoStep2")}
        </h3>
        <p className="text-xs text-parchment-muted mb-3">{t("import.autoStep2Sub")}</p>
        <div className="flex flex-wrap gap-2">
          <a href={apiKeysApi.downloadUrl("mt5")} className="btn-gold" download>
            <Download className="w-4 h-4" />
            {t("import.autoDownloadMt5")}
          </a>
          <a href={apiKeysApi.downloadUrl("mt4")} className="btn-gold" download>
            <Download className="w-4 h-4" />
            {t("import.autoDownloadMt4")}
          </a>
        </div>
        {/* MT5 allows one EA per chart, so anyone already running an EA needs
            the chartless Service build. */}
        <div className="mt-3 rounded-lg border border-glass p-3">
          <p className="text-xs text-parchment mb-2">{t("import.autoServiceNote")}</p>
          <a href={apiKeysApi.downloadUrl("mt5", false, true)} className="btn-ghost" download>
            <Download className="w-4 h-4" />
            {t("import.autoDownloadService")}
          </a>
        </div>

        {/* The binary can't be inspected, so keep the source one click away. */}
        <p className="mt-3 text-xs text-parchment-muted">
          {t("import.autoSourceNote")}{" "}
          <a href={apiKeysApi.downloadUrl("mt5", true)} className="text-link hover:underline" download>
            .mq5
          </a>
          {" · "}
          <a href={apiKeysApi.downloadUrl("mt4", true)} className="text-link hover:underline" download>
            .mq4
          </a>
          {" · "}
          <a
            href={apiKeysApi.downloadUrl("mt5", true, true)}
            className="text-link hover:underline"
            download
          >
            {t("import.autoServiceSource")}
          </a>
        </p>
      </div>

      {/* Step 3 — install */}
      <div className="rounded-xl border border-glass p-4">
        <h3 className="font-semibold text-parchment flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-accent" />
          {t("import.autoStep3")}
        </h3>
        <ol className="mt-2 grid gap-2 text-sm text-parchment-muted list-decimal ms-4">
          <li>{t("import.autoInstall1")}</li>
          <li>
            {t("import.autoInstall3")}{" "}
            <code className="font-mono text-xs text-accent">https://finpip.com</code>
          </li>
          <li>{t("import.autoInstall4")}</li>
        </ol>
        <p className="mt-3 text-xs text-parchment-muted">{t("import.autoServiceSteps")}</p>
        <p className="mt-2 text-xs text-parchment-muted">{t("import.autoSafeNote")}</p>
      </div>
    </div>
  );
}

function ImportRow({ row: i }: { row: Mt5ImportRecord }) {
  const { t } = useI18n();
  const cls =
    i.status === "completed"
      ? "text-pnl-pos"
      : i.status === "failed"
        ? "text-pnl-neg"
        : "text-amber-300";
  return (
    <li className="py-3 flex items-center gap-3">
      <FileSpreadsheet className="w-4 h-4 text-parchment-muted" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-parchment truncate flex items-center gap-2">
          <span className="truncate">{i.filename}</span>
          <span className="shrink-0 rounded border border-glass-mid px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-parchment-muted">
            {i.platform === "mt4" ? "MT4" : "MT5"}
          </span>
        </p>
        <p className="text-xs text-parchment-muted">
          {t("import.rowMeta", {
            date: formatDate(i.finished_at ?? i.created_at),
            inserted: i.trades_inserted,
            skipped: i.trades_skipped,
            wallet: i.wallet_entries_inserted,
          })}
        </p>
        {i.error && <p className="text-xs text-pnl-neg mt-1">{i.error}</p>}
      </div>
      <span className={`text-xs font-bold uppercase tracking-widest ${cls}`}>{i.status}</span>
    </li>
  );
}
